import './AddPromptPage.scss';
import { Button } from 'primereact/button';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import Title from '../../../shared/components/title/Title';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import Loader from '../../../shared/components/common/loader/Loader';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../../shared/routes/path';
import usePopularQuestions from '../../../shared/hooks/prompt/add/UsePopularQuestions';
import { MonitorQuestion } from '../../../shared/models/monitor/MonitorQuestion';
import { normalizeKeyword } from '../../../shared/utils/utils';
import { Tooltip } from 'primereact/tooltip';
import useCanManage from '../../../shared/hooks/account/UseCanManage';
import CustomQuestionRow from './components/custom-question-row/CustomQuestionRow';
import { useGetProject } from '../../../shared/api/hooks/project/useGetProject';
import { useGetSearchVolume } from '../../../shared/api/hooks/project/useGetSearchVolume';
import { formatSearchVolume } from '../../../shared/helpers/project/ProjectHelper';
import { usePutMonitorsForProject } from '../../../shared/api/hooks/monitor/usePutMonitorsForProject';
import ImportPromptsDialog from '../../../shared/components/import-prompts-dialog/ImportPromptsDialog';
import usePermission from '../../../shared/hooks/global/UsePermission';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import usePromptFitDialog from '../../../shared/hooks/prompt/UsePromptFitDialog';
import { allocationRoom, promptsInUse } from '../../../shared/helpers/prompt/promptFit';

interface CustomQuestion {
  text: string;
  checked: boolean;
  disabled: boolean;
  searchVolume?: number | null;
  phrase?: string | null;
}

const AddPromptPage: React.FC = () => {
  let { projectId } = useParams();

  const projectIdNumber = Number(projectId);

  const { t } = useTranslation();

  const isMobile = useIsMobile();
  const { accountResponse, refetchAccountSilently } = useAccount();

  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const { canManage } = useCanManage();
  const projectsAccess = usePermission('PROJECTS');
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([
    { text: '', checked: true, disabled: false },
  ]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [importDialogVisible, setImportDialogVisible] = useState<boolean>(false);

  const { project, refetch: refetchProject } = useGetProject(projectIdNumber);
  // The project's allocation (Agency Hub accounts) caps the free slots next to the account's pool;
  // usage is counted from the project's prompts, the allocation from the project.
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);
  const allocationLeft = allocationRoom(project, promptsInUse(monitorPrompts ?? []));
  const { showPromptFitError } = usePromptFitDialog({
    onRefresh: () => {
      void refetchProject();
    },
  });

  const { fetchSearchVolume, isLoading: isSearchVolumeLoading } = useGetSearchVolume();
  const { addMonitorPrompts, isLoading: isMonitorPromptsLoading } =
    usePutMonitorsForProject(projectIdNumber);
  const [hasSearchVolumeData, setHasSearchVolumeData] = useState(false);

  const BACK_LINK = `${PROJECTS}/${projectId}`;

  const checkedCustomQuestions = useMemo(
    () => customQuestions.filter((q) => q.checked).map((q) => q.text),
    [customQuestions]
  );
  const allCheckedQuestions = useMemo(() => [...checkedCustomQuestions], [checkedCustomQuestions]);

  // Free slots = the account's pool, capped by the project's allocation where one is set. A project
  // without one takes from what the pool has left next to every other reservation (with nothing
  // allocated that is the plain usage).
  const freeSlots = useMemo(() => {
    const accountFree = accountResponse
      ? accountResponse.maxMonitorCount -
        (allocationLeft === null
          ? accountResponse.allocatedMonitorCount
          : accountResponse.createdMonitorCount)
      : 0;
    return allocationLeft === null ? accountFree : Math.min(accountFree, allocationLeft);
  }, [accountResponse, allocationLeft]);
  // The allocation, not the pool, is what closes the form.
  const allocationBinds =
    allocationLeft !== null &&
    accountResponse !== null &&
    allocationLeft < accountResponse.maxMonitorCount - accountResponse.createdMonitorCount;

  const canAddQuestion = useMemo(() => {
    if (!accountResponse) {
      return false;
    }

    const checkedCount = allCheckedQuestions.length;
    return canManage && checkedCount < freeSlots;
  }, [canManage, accountResponse, allCheckedQuestions, freeSlots]);

  const addCustomQuestion = () => {
    setApiError(null);
    if (canAddQuestion) {
      setCustomQuestions((prev) => {
        const newQuestion = { text: '', checked: true, disabled: false };
        const updated = [...prev, newQuestion];

        const willBeCheckedCount = updated.filter((q) => q.checked).length;
        const willBeAtLimit = willBeCheckedCount >= freeSlots;

        updated.forEach((q) => {
          if (!q.checked) {
            q.disabled = willBeAtLimit;
          } else {
            q.disabled = false;
          }
        });

        return updated;
      });
    }
  };

  const onReset = () => {
    setCustomQuestions([{ text: '', checked: true, disabled: false }]);
    setHasSearchVolumeData(false);
    setApiError(null);
  };

  const onCustomQuestionTextChange = (index: number, value: string) => {
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        text: value,
      };
      return updated;
    });
  };

  const onCustomQuestionChange = (index: number, checked: boolean) => {
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index].checked = checked;

      const willBeCheckedCount = updated.filter((q) => q.checked).length;
      const willBeAtLimit = willBeCheckedCount >= freeSlots;

      updated.forEach((q, i) => {
        if (!q.checked) {
          q.disabled = willBeAtLimit;
        } else {
          q.disabled = false;
        }
      });

      return updated;
    });
  };

  const getAllFilledAndCheckedQuestions = () =>
    checkedCustomQuestions.filter((q) => normalizeKeyword(q).trim() !== '');

  const saveQuestion = async () => {
    if (!project) {
      return;
    }

    setCustomQuestions((prev) =>
      prev.filter((q) => normalizeKeyword(q.text).trim() !== '' && q.checked)
    );

    const questions = getAllFilledAndCheckedQuestions();
    if (questions.length === 0) {
      return;
    }

    try {
      const searchVolumeResponses = await fetchSearchVolume({
        ids: [],
        questions,
        firmName: project.firmName,
        projectCountry: project.localization,
      });

      setCustomQuestions((prev) =>
        prev.map((q) => {
          const volumeData = searchVolumeResponses.find((sv) => sv.question === q.text);
          return {
            ...q,
            searchVolume: volumeData?.searchVolume ?? null,
            phrase: volumeData?.phrase ?? null,
          };
        })
      );

      setHasSearchVolumeData(true);
    } catch (error) {
      setApiError(t('errorFetchingSearchVolume'));
    }
  };

  const confirmAndSave = async () => {
    if (!project) {
      return;
    }

    const questions = getAllFilledAndCheckedQuestions();
    if (questions.length === 0) {
      return;
    }

    const monitorQuestions = questions.map((q) => ({
      prompt: q,
      status: 'PENDING',
    })) as MonitorQuestion[];

    try {
      const isPromptsAdded = await addMonitorPrompts({
        monitorQuestions,
        projectId: project.id,
      });

      if (isPromptsAdded) {
        navigate(BACK_LINK);
      } else {
        setApiError(t('errorFetchingSearchVolume'));
      }
    } catch (error) {
      // The allocation rule's rejections get their own dialog; anything else the inline message.
      if (!showPromptFitError(error)) {
        setApiError(t('errorFetchingSearchVolume'));
      }
    }
  };

  useEffect(() => {
    if (!accountResponse) {
      return;
    }

    // `projectsAccess.write` obok limitów konta: bez tego gość wpisujący ten adres z ręki dostawał
    // cały formularz i dowiadywał się o braku uprawnień dopiero przy zapisie (403). Strona trzyma
    // `loading` do czasu rozstrzygnięcia, więc odesłanie nie miga formularzem.
    const canDoIt = Boolean(
      accountResponse &&
        projectsAccess.write &&
        !['BLOCKED', 'DISABLED'].includes(accountResponse.accountStatus) &&
        accountResponse.createdMonitorCount < accountResponse.maxMonitorCount
    );

    if (!canDoIt || isNaN(projectIdNumber)) {
      navigate(BACK_LINK);
    } else {
      setLoading(false);
    }
  }, [accountResponse, projectsAccess.write]);

  if (!accountResponse || loading) {
    return (
      <div className=" page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog className="dialog-box" />
      <div className="add-prompt-page page-content">
        <div className="page-box">
          {/* Telefon: tytuł i import z CSV (sama ikona) w górnym pasku. */}
          <MobilePageHeader
            title={<span>{t('addNewQuestion')}</span>}
            actions={
              accountResponse?.addons?.includes('IMPORT_FROM_CSV') && (
                <button
                  type="button"
                  className="btn btn--sm mobile-header__add"
                  onClick={() => setImportDialogVisible(true)}
                  aria-label={t('importFromCsv')}
                >
                  <span className="ico--download ico--xs" />
                </button>
              )
            }
          />
          {!isMobile && (
          <Title
            isBaseCategory={false}
            title={t('addNewQuestion')}
            children={
              accountResponse?.addons?.includes('IMPORT_FROM_CSV') ? (
                <Button className="btn btn--sm" onClick={() => setImportDialogVisible(true)}>
                  <span className="ico--download ico--xs" />
                  {t('importFromCsv')}
                </Button>
              ) : null
            }
          />
          )}
          <div className="page-box__stats">
            <div className="add-prompt-page__panel">
              <div className="add-prompt-page__questions">
                {customQuestions.map((customQuestion, index) => (
                  <CustomQuestionRow
                    value={customQuestion.text}
                    index={index}
                    isChecked={customQuestion.checked}
                    isDisabled={customQuestion.disabled || isSearchVolumeLoading || hasSearchVolumeData}
                    onCheckboxChange={onCustomQuestionChange}
                    onQuestionChange={onCustomQuestionTextChange}
                    searchVolume={formatSearchVolume(customQuestion.searchVolume)}
                    hasSearchVolumeData={hasSearchVolumeData}
                    isSearchVolumeLoading={isSearchVolumeLoading}
                    canShowPhrase={
                      accountResponse &&
                      accountResponse.isSuperAdmin &&
                      !accountResponse.isLimitedSuperAdmin &&
                      customQuestion.phrase !== null
                    }
                    phrase={customQuestion.phrase || null}
                    key={`custom-question-${index}`}
                  />
                ))}

                {!hasSearchVolumeData && !isSearchVolumeLoading && (
                  <div className="custom-question">
                    <div className="custom-question__row custom-question__row--btn">
                      <label />
                      <Button
                        id="add-new-btn"
                        onClick={addCustomQuestion}
                        type="button"
                        className={`btn btn--link btn--link-black ${
                          canAddQuestion && !hasSearchVolumeData ? '' : 'btn--disabled-tooltip'
                        }`}
                        label={t('addCustomQuestions')}
                        disabled={hasSearchVolumeData}
                      />

                      {!canAddQuestion && !hasSearchVolumeData && (
                        <Tooltip
                          target={`#add-new-btn`}
                          content={
                            allocationBinds
                              ? t('allocationLimitTooltip', {
                                  used: promptsInUse(monitorPrompts ?? []),
                                  allocation: project?.promptAllocation ?? 0,
                                })
                              : t('limitRestriction')
                          }
                          className="tooltip--top"
                          position="top"
                          style={{ width: '220px' }}
                          showDelay={5}
                        />
                      )}
                    </div>
                  </div>
                )}

                <div className="add-prompt-page__actions">
                  {!hasSearchVolumeData && !isSearchVolumeLoading ? (
                    <Button
                      className="btn btn--sm"
                      label={t('next')}
                      disabled={
                        loading ||
                        isSearchVolumeLoading ||
                        getAllFilledAndCheckedQuestions().length === 0
                      }
                      style={{ width: '130px' }}
                      onClick={saveQuestion}
                    />
                  ) : (
                    <>
                      <Button
                        className="btn btn--secondary btn--sm"
                        label={t('reset')}
                        disabled={
                          loading ||
                          isSearchVolumeLoading ||
                          isMonitorPromptsLoading ||
                          getAllFilledAndCheckedQuestions().length === 0
                        }
                        onClick={onReset}
                        style={{ width: '130px' }}
                      />
                      <Button
                        className="btn btn--sm"
                        label={t('save')}
                        disabled={
                          loading ||
                          isSearchVolumeLoading ||
                          isMonitorPromptsLoading ||
                          getAllFilledAndCheckedQuestions().length === 0
                        }
                        onClick={confirmAndSave}
                        style={{ width: '130px' }}
                      >
                        {isMonitorPromptsLoading && <span className="ico--loader ico--xs" />}
                      </Button>
                    </>
                  )}
                  {apiError && <span className="error-message">{apiError}</span>}
                </div>
              </div>
            </div>
            <div className="add-prompt-page__help" />
          </div>
          {/*<div className="page-box__stats">*/}
          {/*  <div className="add-prompt-page__panel">*/}
          {/*    <div>*/}
          {/*      /!*<form onSubmit={handleSubmit(onSubmit)} className="form">*!/*/}
          {/*      /!*  {!projectId && (*!/*/}
          {/*      /!*    <div className="form__row">*!/*/}
          {/*      /!*      <div className="form__label">{t('keyword')}:</div>*!/*/}
          {/*      /!*      <InputText*!/*/}
          {/*      /!*        {...register('keyword', { required: 'keyword is required' })}*!/*/}
          {/*      /!*        className={errors.keyword ? 'form__invalid' : ''}*!/*/}
          {/*      /!*      />*!/*/}
          {/*      /!*      {errors.keyword && <span>{errors.keyword.message as string}</span>}*!/*/}
          {/*      /!*    </div>*!/*/}
          {/*      /!*  )}*!/*/}
          {/*      /!*  <div className="form__row">*!/*/}
          {/*      /!*    <div className="form__label">{t('prompt')}:</div>*!/*/}
          {/*      /!*    <InputTextarea*!/*/}
          {/*      /!*      {...register('prompt', { required: 'Prompt is required' })}*!/*/}
          {/*      /!*      className={errors.prompt ? 'form__invalid' : ''}*!/*/}
          {/*      /!*      disabled={monitorData !== null}*!/*/}
          {/*      /!*      onChange={(e) => onChangePrompt(e.target.value)}*!/*/}
          {/*      /!*    />*!/*/}
          {/*      /!*    {isConflictName && (*!/*/}
          {/*      /!*      <div className="add-prompt-page__warning">*!/*/}
          {/*      /!*        <span className="material-symbols-outlined" id={`warming-icon`}>*!/*/}
          {/*      /!*          warning*!/*/}
          {/*      /!*        </span>*!/*/}
          {/*      /!*        {t('brandNameRestriction')}*!/*/}
          {/*      /!*      </div>*!/*/}
          {/*      /!*    )}*!/*/}
          {/*      /!*    {errors.prompt && <span>{errors.prompt.message as string}</span>}*!/*/}
          {/*      /!*  </div>*!/*/}
          {/*      /!*  {popularQuestions.length > 0 && (*!/*/}
          {/*      /!*    <div className="add-prompt-page__popular-question">*!/*/}
          {/*      /!*      <div className="add-prompt-page__subtitle">{t('popularQuestions')}:</div>*!/*/}
          {/*      /!*      <div className="add-prompt-page__tag-panel">*!/*/}
          {/*      /!*        {popularQuestions.map((question, index) => (*!/*/}
          {/*      /!*          <div>*!/*/}
          {/*      /!*            <span*!/*/}
          {/*      /!*              className={`add-prompt-page__tag`}*!/*/}
          {/*      /!*              key={`popular-question-${index}`}*!/*/}
          {/*      /!*              onClick={() => setValue('prompt', question)}*!/*/}
          {/*      /!*            >*!/*/}
          {/*      /!*              {question}*!/*/}
          {/*      /!*            </span>*!/*/}
          {/*      /!*          </div>*!/*/}
          {/*      /!*        ))}*!/*/}
          {/*      /!*      </div>*!/*/}
          {/*      /!*    </div>*!/*/}
          {/*      /!*  )}*!/*/}

          {/*      /!*  <div>*!/*/}
          {/*      /!*    {searchVolume && (*!/*/}
          {/*      /!*      <div className="add-prompt-page__search-keyword">*!/*/}
          {/*      /!*        <div>{t('searchVolume')}:</div>*!/*/}
          {/*      /!*        <div>*!/*/}
          {/*      /!*          {searchVolume[0].searchVolume > 100*!/*/}
          {/*      /!*            ? Intl.NumberFormat('pl-PL').format(searchVolume[0].searchVolume)*!/*/}
          {/*      /!*            : '<100'}*!/*/}
          {/*      /!*        </div>*!/*/}
          {/*      /!*        {accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin && (*!/*/}
          {/*      /!*          <span className="add-prompt-page__phrase">{searchVolume[0].phrase}</span>*!/*/}
          {/*      /!*        )}*!/*/}
          {/*      /!*      </div>*!/*/}
          {/*      /!*    )}*!/*/}
          {/*      /!*    <div className="form__info ">{t('historyDataInfo')}</div>*!/*/}
          {/*      /!*  </div>*!/*/}

          {/*      /!*  <Button*!/*/}
          {/*      /!*    className="btn btn--sm"*!/*/}
          {/*      /!*    label={t('next')}*!/*/}
          {/*      /!*    disabled={loading}*!/*/}
          {/*      /!*    hidden={monitorData !== null}*!/*/}
          {/*      /!*  />*!/*/}
          {/*      /!*</form>*!/*/}

          {/*      <div className="add-prompt-page__actions">*/}
          {/*        <Button*/}
          {/*          className="btn btn--sm btn--secondary"*/}
          {/*          label={t('reset')}*/}
          {/*          disabled={loading}*/}
          {/*          hidden={monitorData === null}*/}
          {/*          onClick={resetData}*/}
          {/*        />*/}
          {/*        <Button*/}
          {/*          className="btn btn--sm"*/}
          {/*          label={t('save')}*/}
          {/*          disabled={loading}*/}
          {/*          hidden={monitorData === null}*/}
          {/*          onClick={saveQuestion}*/}
          {/*        />*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*    <div className="add-prompt-page__help" />*/}
          {/*  </div>*/}
          {/*</div>*/}
        </div>
      </div>
      <ImportPromptsDialog
        visible={importDialogVisible}
        onHide={() => setImportDialogVisible(false)}
        projectId={projectIdNumber}
        availableSlots={Math.max(
          0,
          (accountResponse?.maxMonitorCount ?? 0) - (accountResponse?.createdMonitorCount ?? 0)
        )}
        onSuccess={() => {
          refetchAccountSilently();
          navigate(BACK_LINK);
        }}
      />
    </>
  );
};

export default AddPromptPage;
